import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import BuyerPublicNavbar from "@/components/BuyerPublicNavbar";
import api from "@/lib/api";
import { BLOG_AUTHOR_LABEL, estimateReadMinutes } from "@/lib/blogReadTime";

const URL_REGEX = /(https?:\/\/[^\s<>"']+)/g;

function LinkedText({ text, className }) {
  if (!text) return null;
  const parts = text.split(URL_REGEX);
  return (
    <span className={className}>
      {parts.map((part, i) =>
        URL_REGEX.test(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#FF7A00] underline underline-offset-2 hover:opacity-80 break-all"
          >
            {part}
          </a>
        ) : (
          part
        )
      )}
    </span>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get(`/blog/posts/${encodeURIComponent(slug)}`);
        if (!cancelled) setPost(r.data);
      } catch (e) {
        if (!cancelled) {
          setPost(null);
          setError(e.response?.status === 404 ? "notfound" : "error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const readMinutes = useMemo(
    () => (post ? estimateReadMinutes(post.body, post.excerpt) : 1),
    [post]
  );

  const formattedDate = post?.updated_at
    ? new Date(post.updated_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen bg-white">
      <BuyerPublicNavbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-24 sm:pt-12">
        <Link
          to="/blog"
          className="text-sm font-semibold text-[#FF7A00] hover:text-[#e86800] mb-10 inline-block"
        >
          ← Back to articles
        </Link>

        {loading ? (
          <p className="text-neutral-500 text-sm">Loading…</p>
        ) : error === "notfound" ? (
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Article not found</h1>
            <p className="mt-2 text-neutral-600 text-sm leading-relaxed">
              This article may have been removed or the link is incorrect.
            </p>
          </div>
        ) : error ? (
          <p className="text-neutral-600 text-sm">Could not load this article. Try again later.</p>
        ) : post ? (
          <article>
            <p className="text-[#FF7A00] text-[11px] sm:text-xs font-bold tracking-[0.22em] uppercase mb-3">
              SCAFFOINDIA BLOG
            </p>
            <h1 className="text-3xl sm:text-[2.5rem] font-bold tracking-tight text-neutral-900 leading-tight">
              {post.title}
            </h1>
            <p className="mt-4 text-sm text-neutral-500">
              {readMinutes} min read <span className="text-neutral-300 px-1">·</span> {BLOG_AUTHOR_LABEL}
              {formattedDate ? (
                <>
                  <span className="text-neutral-300 px-1">·</span>
                  {formattedDate}
                </>
              ) : null}
            </p>
            {post.excerpt ? (
              <p className="mt-6 text-neutral-600 leading-relaxed text-base border-l-2 border-[#FF7A00]/30 pl-4">
                <LinkedText text={post.excerpt} />
              </p>
            ) : null}
            <div className="mt-8 text-neutral-800 leading-[1.75] whitespace-pre-wrap text-base">
              <LinkedText text={post.body} />
            </div>
          </article>
        ) : null}
      </main>
    </div>
  );
}
